import { LoadingButton } from '@mui/lab'
import { Button, Dialog, DialogActions, DialogContent, Typography } from '@mui/material'
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form'
import { toast } from 'react-toastify'

import { FplDialogTitle } from 'components/common'
import { Mode } from 'constants/index'
import {
  CreateAndUpdateTariffCategoryDtoInput,
  useCreateTariffCategoryMutation,
  useUpdateTariffCategoryMutation,
} from 'generated/graphql'

import TariffCategoryForm from './TariffCategoryForm'
import UpdateTariffCategory from './UpdateTariffCategory'

interface IProps {
  id: number | null
  openDialog: boolean
  onCloseDialog: () => void
  fplDataGridActionsRef: React.MutableRefObject<any>
}

const TariffCategoryDialog = (props: IProps) => {
  const { id, openDialog, onCloseDialog, fplDataGridActionsRef } = props

  const mode = id === null ? Mode.Create : Mode.Update

  const methods = useForm<CreateAndUpdateTariffCategoryDtoInput>({
    shouldUnregister: true,
    defaultValues: {
      name: '',
      vehicleTypeId: '',
      hasSpotRate: false,
    },
  })

  const { handleSubmit } = methods

  // Mutations
  const [createTariffCategory, { loading: creatingTariffCategory }] =
    useCreateTariffCategoryMutation({
      onCompleted: (data) => {
        if (data.createTariffCategory) {
          toast.success(
            `Tariff Category (${data.createTariffCategory?.id}) was created with success`,
          )
          onCloseDialog()
          fplDataGridActionsRef.current.queryRefetchOnTableUpdate()
        }
      },
    })

  const [updateTariffCategory, { loading: updatingTariffCategory }] =
    useUpdateTariffCategoryMutation({
      onCompleted: (data) => {
        if (data.updateTariffCategory) {
          toast.success(
            `Tariff Category (${data.updateTariffCategory?.id}) was updated with success`,
          )
          onCloseDialog()
        }
      },
    })

  const onSubmitTariffCategory: SubmitHandler<CreateAndUpdateTariffCategoryDtoInput> = ({
    name,
    vehicleTypeId,
    hasSpotRate,
  }) => {
    const resolvedVehicleTypeId = vehicleTypeId === '' ? null : vehicleTypeId

    if (mode === Mode.Create) {
      createTariffCategory({
        variables: {
          input: {
            name,
            vehicleTypeId: resolvedVehicleTypeId,
            hasSpotRate,
          },
        },
      })

      return
    }

    updateTariffCategory({
      variables: {
        input: {
          id: Number(id),
          name,
          vehicleTypeId: resolvedVehicleTypeId,
          hasSpotRate,
        },
      },
    })
  }

  const handleClose = () => {
    onCloseDialog()
  }

  return (
    <Dialog
      fullWidth
      maxWidth='md'
      scroll='body'
      open={openDialog}
      aria-labelledby='tariff-category-dialog'>
      <FplDialogTitle id='tariff-category-dialog' onClose={handleClose}>
        <Typography paragraph variant='h4'>
          {mode === Mode.Create ? 'Create' : 'Update'} Tariff Category
        </Typography>
      </FplDialogTitle>

      <DialogContent>
        <FormProvider {...methods}>
          {mode === Mode.Create ? <TariffCategoryForm /> : <UpdateTariffCategory id={id!} />}
        </FormProvider>
      </DialogContent>

      <DialogActions>
        <Button variant='contained' color='grey' onClick={handleClose}>
          Cancel
        </Button>

        <LoadingButton
          variant='contained'
          loading={creatingTariffCategory || updatingTariffCategory}
          onClick={handleSubmit(onSubmitTariffCategory)}>
          Save
        </LoadingButton>
      </DialogActions>
    </Dialog>
  )
}

export default TariffCategoryDialog
